import React, { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { observer } from 'mobx-react-lite';
import { CSS } from '@dnd-kit/utilities';
import { useDraggable } from '@dnd-kit/core';
import * as yup from 'yup';
import { FilePreviewAudio } from '@producer/ui-kit';

import { FileInfo } from './FileInfo';
import { FilePreview } from '../../../components/icons/FilePreview';
import { LinkPreview } from '../../../components/icons/LinkPreview';
import { DownloadOverlay } from './download-overlay';
import { FILES_TYPES } from '../../../assets/enums/assetType.enum';
import { Add } from '../../../components/icons';
import { getModifier } from '../../../core/services/event.service';
import { useSelection } from '../../../core/contexts/selection.context';
import { secondsToHms } from '../../../lib/utils/Convertor';

import { Asset } from '../../../app/entities';
import { context } from '../../../app/context';
import { toastPool } from '../../toasts/models/toast-pool';
import { Toast } from '../../toasts/models/toast';
import { catchError } from '../../../core/catch-error';
import { AssetMenu } from './asset-menu';
import { useResponsive } from '../../../hooks/useResponsive';
import { useLongTouch } from '../../../app/hooks/use-long-touch';
import { TagsViewer } from '../../../features/tags/components/tags-viewer';
import { CommentCount } from '../../comments/components/comment-count';

import styles from './file-tile.module.scss';
type FileItemProps = {
  asset: Asset;
  assetsRouteTo?: (asset: string, assetId: string) => void;
  isSelected?: boolean;
};

const RenderCover: React.FC<{ file: Asset }> = ({ file }) => {
  if (file.type === 'link') {
    const previewImage = file?.meta?.previewImage;
    const favIcon = file?.meta?.favIcon;

    return previewImage || favIcon ? (
      <div className={styles['files-body-item-image']} data-hasLogo={!!favIcon && !previewImage}>
        {previewImage ? (
          <>
            <div className={styles['link-icon-wrapper']} data-onTopOfPreviewImage>
              <LinkPreview />
            </div>
            <img className={styles['image-cover']} src={previewImage} alt={file.name} />
          </>
        ) : (
          <div
            className={styles['files-image-mp4']}
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <img className={styles['image-cover']} src={favIcon} alt={file.name} />
          </div>
        )}
      </div>
    ) : (
      <div className={styles['files-image-mp4']}>
        <div className={styles['link-icon-wrapper']}>
          <LinkPreview />
        </div>
      </div>
    );
  } else if (file.type === 'note') {
    return (
      <div className={styles['files-image-mp4']}>
        <FilePreview text="note" />
      </div>
    );
  } else if (file.fileType?.includes(FILES_TYPES.AUDIO)) {
    return (
      <div className={styles['files-image-mp4']}>
        <FilePreviewAudio />
      </div>
    );
  } else if (
    file.fileType?.includes(FILES_TYPES.IMAGE) ||
    file.fileType?.includes('png') ||
    file.fileType?.includes('jpeg')
  ) {
    return (
      <div className={styles['files-body-item-image']}>
        {file.cover ? (
          <img className={styles['image-cover']} src={file.cover.src} alt={file.name} />
        ) : (
          <FilePreview text={file.extension} />
        )}
      </div>
    );
  } else if (file.fileType?.includes(FILES_TYPES.VIDEO)) {
    return (
      <div className={styles['files-body-item-image']}>
        <img className={styles['image-cover']} src={file.cover?.src} alt={file.name} />
        {
          <span className={styles['video-duration']}>
            {file.meta?.duration ? secondsToHms(file.meta.duration) : 'MOVIE'}
          </span>
        }
      </div>
    );
  }

  return (
    <div className={styles['files-image-mp4']}>
      <FilePreview text={file.extension} />
    </div>
  );
};

export const FileTile: React.FC<FileItemProps> = observer(
  ({ asset, assetsRouteTo, isSelected }) => {
    const { select, toggleSelectMode, selectionModeOn } = useSelection();
    const { isMobile } = useResponsive();
    const { longTouchRef } = useLongTouch({
      onLongTouch: () => toggleSelectMode(asset._id),
    });

    const { setNodeRef, attributes, listeners, transform, isDragging } = useDraggable({
      id: asset._id,
      data: {
        type: 'file',
      },
    });

    const style = {
      transform: CSS.Transform.toString(transform),
    };

    const handleNewVersion = async (file: File) => {
      if (!asset.uploaded) {
        return;
      }

      try {
        await asset.addVersion(file);
      } catch (e) {
        catchError(e);
      }
    };

    const onDrop = (files: File[]) => {
      handleNewVersion(files[0]);
    };

    const accepts = useMemo(() => {
      switch (asset.fileType) {
        case 'image/*':
          return {
            'image/png': ['.png', '.jpg', '.jpeg'],
          };
        case 'audio/*':
          return {
            'audio/mpeg': ['.mp3'],
          };
        case 'video/*':
          return {
            'video/mp4': ['.mp4', '.mov', '.avi', '.mkv'],
          };
        case 'application/pdf':
          return {
            'application/pdf': ['.pdf'],
          };
        case 'application/zip':
          return {
            'application/zip': ['.zip'],
          };
      }
    }, [asset.fileType]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      noClick: true,
      noDragEventsBubbling: true,
      accept: accepts as Record<string, string[]> | undefined,
    });

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!isMobile || selectionModeOn) {
        const modifier = getModifier(e.nativeEvent);
        select(asset._id, modifier);
        return;
      }

      handleOpenFile();
    };

    const handleOpenFile = () => {
      if (asset.type === 'link') {
        const hasValidSchema = new RegExp(/^https?:\/\//).test(asset.url!);
        const url = hasValidSchema ? asset.url : `https://${asset.url}`;

        yup
          .string()
          .url()
          .validate(url)
          .then(() => {
            const newTab = window.open(url, '_blank');
            newTab?.focus();
          })
          .catch(() => {
            toastPool.insert(new Toast('Invalid URL.', 'error'));
          });

        return;
      }

      if (asset.uploaded === false && context.member?._id !== asset.uploaderId) {
        toastPool.insert(new Toast('File is not uploaded yet.', 'error'));
        return;
      }

      return assetsRouteTo && asset && assetsRouteTo('file', asset._id);
    };

    if (asset.type === 'folder') {
      return (
        <article
          className={styles['container']}
          onDoubleClick={handleOpenFile}
          onClick={handleClick}
          data-selected={isSelected}
        >
          <div className={styles['file-header']} />

          <footer className={styles['file-footer']}>
            <div className={styles['file-name']}>{asset.name}</div>
          </footer>

          <div className={styles['modal-container']}>
            <AssetMenu asset={asset} onOpen={handleOpenFile} />
          </div>
        </article>
      );
    }

    return (
      <article
        className={styles['container']}
        key={asset?._id + (asset?.meta?.previewImage || '')} // To rerender the preview image immediately after updating the url
        onDoubleClick={handleOpenFile}
        {...getRootProps()}
        onClick={handleClick}
        data-selected={isSelected}
        ref={(e) => {
          setNodeRef(e);
          longTouchRef.current = e;
        }}
        style={style}
        data-dragging={isDragging}
        {...attributes}
        {...listeners}
      >
        <input {...getInputProps()} />
        {asset.currentVersionNumber && (
          <div className={styles['current-version-display']}>
            <span>v</span>
            {asset.currentVersionNumber}
          </div>
        )}

        <div className={styles['file-header']}>
          <div className={styles['file-cover']}>
            {isDragActive && asset.type !== 'link' && (
              <div className={styles['drag-overlay']}>
                <div className={styles['drop-icon-wrapper']}>
                  <Add />
                </div>

                <div className={styles['drag-overlay-text']}>
                  Add Version
                  <br />
                  of same file type
                </div>
              </div>
            )}

            <RenderCover file={asset} />

            {asset.uploaded === false &&
              (asset.uploaderId &&
              context.member?._id &&
              asset.uploaderId === context.member._id ? (
                asset.upload ? (
                  <div className={styles['progress-loader']}>
                    <DownloadOverlay
                      progress={asset.upload.progress}
                      status={asset.upload.status}
                      onPause={() => {
                        asset.upload!.pause();
                      }}
                      onResume={() => {
                        asset.upload!.resume();
                      }}
                    />
                  </div>
                ) : (
                  <div className={styles['blocked-uploading-overlay']}>
                    <span>resuming files from different machines is not supported yet</span>
                  </div>
                )
              ) : (
                <div className={styles['blocked-uploading-overlay']}>
                  <span>This file is being uploaded by another user</span>
                </div>
              ))}
          </div>

          <div className={styles['modal-container']}>
            {asset.uploaded === false && context.member?._id !== asset.uploaderId ? null : (
              <AssetMenu asset={asset} onOpen={handleOpenFile} />
            )}
          </div>
        </div>

        <footer className={styles['file-footer']}>
          <div className={styles['file-name-wrapper']}>
            <div className={styles['file-name']}>{asset.name}</div>

            {!['link', 'folder'].includes(asset.type) && (
              <CommentCount count={asset.commentCount} />
            )}
          </div>

          <FileInfo file={asset} />

          <div style={{ padding: '0.8rem 0' }}>
            <TagsViewer
              targetId={asset._id}
              targetType="asset"
              alignment="start"
              overflow="collapse"
            />
          </div>
        </footer>
      </article>
    );
  },
);
